/**
 * Preview copy for the SMS & Phone Disclaimer "View Text" modals.
 *
 * The Control Panel stores only the disclaimer type — the text that visitors actually see is
 * rendered by the frontends. These strings exist purely so an agent can preview the wording
 * before saving, and they must stay in sync with the canonical copies:
 *
 *   - ihf-root  web-applications/listingWebApp/src/main/resources/messages.properties
 *               (label.subscriber.signup.policy.blurb / .policy.fub)
 *   - kestrel   src/component/shared/lead-sign-up-policy/lang.ts
 *   - eureka    src/app/component/lead-sign-up-policy/lead-sign-up-policy.component.html
 *
 * The Follow Up Boss wording is fixed and approved by Follow Up Boss — do not edit without
 * Product sign-off.
 */

const BUSINESS_NAME_PLACEHOLDER = "[Business Name]";

/** Substitutes the account's display name, falling back to a visible placeholder. */
const withBusinessName = (template: string, displayName?: string) =>
	template.replace(/\{businessName\}/g, displayName || BUSINESS_NAME_PLACEHOLDER);

export const standardDisclaimerText = (displayName?: string) => withBusinessName(
	"By selecting this checkbox and entering your mobile number, you agree to receive marketing " +
	"text messages and calls from {businessName}, including messages about real estate listings, " +
	"property recommendations, and related services. Message and data rates may apply. Message " +
	"frequency varies. Reply HELP for help or STOP to cancel.",
	displayName,
);

export const followUpBossDisclaimerText = (displayName?: string) => withBusinessName(
	"I agree to be contacted by {businessName} via call, email, and text for real estate " +
	"services. To opt out, you can reply 'stop' at any time or reply 'help' for assistance. You " +
	"can also click the unsubscribe link in the emails. Message and data rates may apply. " +
	"Message frequency may vary.",
	displayName,
);

/**
 * The standard disclaimer is followed by a second consent checkbox whenever a Privacy Policy or
 * Terms of Service URL is configured. The label adapts to whichever URLs are present.
 */
export const privacyConsentText = (privacyPolicyUrl?: string, termsOfServiceUrl?: string) => {
	if (privacyPolicyUrl && termsOfServiceUrl) {
		return "By checking, I accept the Privacy Policy & Terms of Service.";
	}
	if (privacyPolicyUrl) {
		return "By checking, I accept the Privacy Policy.";
	}
	if (termsOfServiceUrl) {
		return "By checking, I accept the Terms of Service.";
	}
	return undefined;
};

export const SMS_DISCLAIMER_TYPE_STANDARD = "standard";
export const SMS_DISCLAIMER_TYPE_FOLLOW_UP_BOSS = "followUpBoss";

/** Radio option labels and descriptions. Copy per the Q2 2026 designs. */
export const standardOptionLabel = "Standard Disclaimer";
export const standardOptionDescription =
	"Shows an SMS consent checkbox, plus a Privacy Policy & Terms of Service checkbox if links are added below.";

export const followUpBossOptionLabel = "Follow Up Boss–compliant disclaimer";
export const followUpBossOptionDescription =
	"Shows a single checkbox with Follow Up Boss–approved wording and an inline Privacy Policy link. Requires a Privacy Policy URL below.";

/** Shown in place of the toggle when the client uses iHomefinder texting. */
export const textingEnabledDescription =
	"Because you're using iHomeFinder's texting service, a disclaimer always appears on your lead capture forms. Choose which version to show below.";

/** Preview modal titles and intros. */
export const standardPreviewTitle = "Standard Disclaimer";
export const standardPreviewIntro =
	"At the bottom of your lead capture forms, the following text will display. If you've added Privacy Policy and Terms of Service links, a second checkbox will also appear:";

export const followUpBossPreviewTitle = "FUB-compliant Disclaimer";
export const followUpBossPreviewIntro =
	"At the bottom of your lead capture forms, the following text will display:";

export const privacyPolicyUrlRequiredError = "Enter a Privacy Policy URL to use FUB-compliant disclaimer.";
export const reviewErrorsMessage = "Please review errors above before saving changes.";
